import { useMemo, useState } from "react";

type Pillar = {
  id: string;
  name: string;
  weight: number;
  blurb: string;
  checks: { id: string; label: string; hint: string }[];
};

const PILLARS: Pillar[] = [
  {
    id: "crawl",
    name: "Crawl & Index",
    weight: 0.22,
    blurb: "If a page can't be reached and stored cleanly, nothing else you do matters.",
    checks: [
      { id: "c1", label: "Self-referencing canonical on every page", hint: "Conflicting canonicals let Google pick its own." },
      { id: "c2", label: "Sitemap lists only indexable URLs", hint: "Mixed sitemaps waste crawl budget." },
      { id: "c3", label: "Money pages within 3 clicks of home", hint: "Depth correlates with crawl frequency." },
      { id: "c4", label: "One redirect hop, apex host, consistent slash", hint: "Chains leak speed and clarity." },
    ],
  },
  {
    id: "render",
    name: "Render & Speed",
    weight: 0.2,
    blurb: "Content that arrives late — or paints late — loses both rankings and users.",
    checks: [
      { id: "r1", label: "Primary content in the server HTML", hint: "Client-only content is discovered late or never." },
      { id: "r2", label: "LCP image preloaded, AVIF/WebP, sized", hint: "The single biggest mobile LCP lever." },
      { id: "r3", label: "Third-party tags deferred past first input", hint: "Analytics and chat are the usual INP tax." },
      { id: "r4", label: "Mobile CLS under 0.1 at 360px", hint: "Mobile is where CLS actually fails." },
    ],
  },
  {
    id: "structure",
    name: "Structure & Schema",
    weight: 0.18,
    blurb: "Machine-readable structure is how search engines and assistants parse meaning.",
    checks: [
      { id: "s1", label: "One H1, no skipped heading levels", hint: "Outline parsing feeds both a11y and LLMs." },
      { id: "s2", label: "Organization + LocalBusiness schema", hint: "Entity grounding for the Knowledge Graph." },
      { id: "s3", label: "BreadcrumbList on every deep page", hint: "Improves SERP display and context." },
      { id: "s4", label: "FAQ/Article schema matches visible content", hint: "Invisible markup risks a manual action." },
    ],
  },
  {
    id: "content",
    name: "Content & Intent",
    weight: 0.22,
    blurb: "One page per intent, answered directly, with evidence a buyer can check.",
    checks: [
      { id: "t1", label: "No two pages target the same primary query", hint: "Cannibalisation splits ranking signals." },
      { id: "t2", label: "First 100 words answer the query", hint: "Answer-first blocks are what get extracted." },
      { id: "t3", label: "Every claim has a source or first-party datapoint", hint: "Unsourced claims rarely get cited." },
      { id: "t4", label: "Pillars supported by 4+ cluster pages", hint: "Coverage beats isolated long pages." },
    ],
  },
  {
    id: "authority",
    name: "Authority & Local",
    weight: 0.18,
    blurb: "Corroboration off your own domain is what makes the on-page work credible.",
    checks: [
      { id: "a1", label: "NAP identical across site, GBP, and citations", hint: "Inconsistency suppresses Map Pack placement." },
      { id: "a2", label: "Steady review velocity, all answered", hint: "Bursts look manufactured and get filtered." },
      { id: "a3", label: "Author entities with credentials and sameAs", hint: "Attribution strengthens citation likelihood." },
      { id: "a4", label: "Genuinely local links earned this quarter", hint: "Chamber, supplier, sponsorship, local press." },
    ],
  },
];

const TOTAL_CHECKS = PILLARS.reduce((n, p) => n + p.checks.length, 0);

function verdict(score: number) {
  if (score >= 85) return { label: "Compounding", note: "Foundations are solid. Growth now comes from coverage and authority, not fixes." };
  if (score >= 65) return { label: "Functional", note: "Nothing is on fire, but the gaps below are what's capping your ceiling." };
  if (score >= 40) return { label: "Leaking", note: "You're paying for content and links that the foundation can't convert into rankings." };
  return { label: "Blocked", note: "Fix the unchecked items before spending another dollar on content or links." };
}

/**
 * Interactive SEO audit visualisation. Purely educational and client-side:
 * tick what's true on your site, watch the weighted score and per-pillar
 * bars respond, and read the prioritised gap list it produces.
 */
export function AuditVisualizer() {
  const [done, setDone] = useState<Record<string, boolean>>({});
  const [open, setOpen] = useState<string>(PILLARS[0].id);

  const scores = useMemo(() => {
    const per = PILLARS.map((p) => {
      const hit = p.checks.filter((c) => done[c.id]).length;
      return { pillar: p, pct: Math.round((hit / p.checks.length) * 100), hit };
    });
    const weighted = Math.round(per.reduce((n, s) => n + s.pct * s.pillar.weight, 0));
    return { per, weighted };
  }, [done]);

  const gaps = PILLARS.flatMap((p) =>
    p.checks.filter((c) => !done[c.id]).map((c) => ({ pillar: p, check: c })),
  ).sort((a, b) => b.pillar.weight - a.pillar.weight);

  const v = verdict(scores.weighted);
  const checkedCount = TOTAL_CHECKS - gaps.length;

  return (
    <div className="rounded-3xl border border-border bg-card/40 p-6 md:p-10">
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          <div className="space-y-3">
            {PILLARS.map((p) => {
              const s = scores.per.find((x) => x.pillar.id === p.id)!;
              const expanded = open === p.id;
              return (
                <div key={p.id} className="rounded-2xl border border-border/70 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpen(expanded ? "" : p.id)}
                    aria-expanded={expanded}
                    className="w-full px-5 py-4 text-left"
                  >
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="font-display text-lg">{p.name}</span>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {s.hit}/{p.checks.length} · {Math.round(p.weight * 100)}% weight
                      </span>
                    </div>
                    <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary transition-[width] duration-500 motion-reduce:transition-none"
                        style={{ width: `${s.pct}%` }}
                      />
                    </div>
                  </button>
                  {expanded && (
                    <div className="border-t border-border/70 px-5 py-4">
                      <p className="text-sm text-muted-foreground">{p.blurb}</p>
                      <ul className="mt-4 space-y-3">
                        {p.checks.map((c) => (
                          <li key={c.id}>
                            <label className="flex cursor-pointer items-start gap-3">
                              <input
                                type="checkbox"
                                checked={!!done[c.id]}
                                onChange={(e) =>
                                  setDone((d) => ({ ...d, [c.id]: e.target.checked }))
                                }
                                className="mt-1 h-4 w-4 accent-[var(--color-primary,#e31e24)]"
                              />
                              <span>
                                <span className="text-sm text-foreground">{c.label}</span>
                                <span className="block text-xs text-muted-foreground">{c.hint}</span>
                              </span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-border/70 bg-background/60 p-6 text-center">
            <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
              Weighted readiness
            </div>
            <div className="mt-2 font-display text-6xl tabular-nums text-primary">
              {scores.weighted}
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              {checkedCount} of {TOTAL_CHECKS} checks in place
            </div>
            <div className="mt-5 rounded-xl border border-border/70 p-4 text-left">
              <div className="text-xs uppercase tracking-widest text-primary">{v.label}</div>
              <p className="mt-2 text-sm text-muted-foreground">{v.note}</p>
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-border/70 p-5">
            <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
              Fix next (weighted)
            </div>
            {gaps.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">
                Nothing outstanding. At this point growth comes from topical coverage and links.
              </p>
            ) : (
              <ol className="mt-3 space-y-2 text-sm">
                {gaps.slice(0, 5).map(({ pillar, check }) => (
                  <li key={check.id} className="flex gap-2">
                    <span aria-hidden className="text-primary">
                      →
                    </span>
                    <span>
                      <span className="text-foreground">{check.label}</span>
                      <span className="block text-xs text-muted-foreground">{pillar.name}</span>
                    </span>
                  </li>
                ))}
              </ol>
            )}
            {gaps.length > 5 && (
              <p className="mt-3 text-xs text-muted-foreground">
                +{gaps.length - 5} more once these are done.
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => setDone({})}
            className="mt-4 w-full rounded-full border border-border px-4 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}
